import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { bool, func, object, number, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { propTypes } from '../../../../util/types';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  CartIconBadge,
} from '../../../../components';

import { Link } from '../../../PageBuilder/Primitives/Link';

import css from './TopbarDesktop.module.css';

import NotificationsIcon from '../../../../assets/notification-icon.svg';
import ProfileIcon from '../../../../assets/profile-icon.svg';
import {
  isApprovedSeller,
  isApprovedOwner,
  hasGroups,
  USER_TYPES,
  isSeller,
  isGuestUser,
} from '../../../../util/userHelpers';

// Set to true to reactivate the "Book Services" topbar link
const SHOW_SERVICES_LINK = false;

const TopbarDesktop = props => {
  const {
    className,
    appConfig,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues,
    isLanding,
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = appConfig.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const classes = classNames(rootClassName || css.root, className);

  const mainNav = (
    <div className={css.mainNav}>
      <Menu useArrow={false}>
        <MenuLabel className={css.mainNavLabel} isOpenClassName={css.mainNavLabelOpen}>
          BUY GEAR
        </MenuLabel>
        <MenuContent className={css.mainNavDropdown}>
          <MenuItem key="archery">
            <a href="/s?pub_category=archery" className={css.mainNavDropdownLink}>Archery</a>
          </MenuItem>
          <MenuItem key="fishing">
            <a href="/s?pub_category=fishing" className={css.mainNavDropdownLink}>Fishing</a>
          </MenuItem>
          <MenuItem key="hunting">
            <a href="/s?pub_category=hunting" className={css.mainNavDropdownLink}>Hunting &amp; Outdoors</a>
          </MenuItem>
        </MenuContent>
      </Menu>

      {SHOW_SERVICES_LINK && (
        <a
          href="/services"
          className={classNames(css.mainNavLabel, {
            [css.mainNavLabelOpen]: currentPage === 'ServicesSearchPage',
          })}
        >
          <FormattedMessage id="TopbarDesktop.bookServicesLink" />
        </a>
      )}

      <a
        href="/dealers"
        className={classNames(css.mainNavLabel, {
          [css.mainNavLabelOpen]: currentPage === 'DealersDirectoryPage',
        })}
      >
        DEALER LOCATOR
      </a>

      <Menu useArrow={false}>
        <MenuLabel className={css.mainNavLabel} isOpenClassName={css.mainNavLabelOpen}>
          SELL GEAR
        </MenuLabel>
        <MenuContent className={css.mainNavDropdown}>
          <MenuItem key="become-seller">
            <a href="/become-a-seller" className={css.mainNavDropdownLink}>Become a Seller</a>
          </MenuItem>
          <MenuItem key="edge">
            <a href="/p/edge" className={css.mainNavDropdownLink}>BowTackle Edge</a>
          </MenuItem>
          <MenuItem key="epro">
            <a href="/p/epro" className={css.mainNavDropdownLink}>ePro Integration</a>
          </MenuItem>
        </MenuContent>
      </Menu>

      <Menu useArrow={false}>
        <MenuLabel className={css.mainNavLabel} isOpenClassName={css.mainNavLabelOpen}>
          DISCOVER
        </MenuLabel>
        <MenuContent className={css.mainNavDropdown}>
          <MenuItem key="ethos">
            <a href="/p/ethos" className={css.mainNavDropdownLink}>Ethos</a>
          </MenuItem>
          <MenuItem key="blog">
            <a href="/p/blog" className={css.mainNavDropdownLink}>Blog</a>
          </MenuItem>
        </MenuContent>
      </Menu>
    </div>
  );

  const searchIcon = (
    <a href="/s" className={css.searchIconButton}>
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    </a>
  );

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const inboxLink = authenticatedOnClientSide ? (
    <NamedLink
      className={classNames(css.inboxLink, css.notificationsContainer)}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      <div className={css.notificationsImageContainer}>
        <img src={NotificationsIcon} />
        {notificationDot}
      </div>
      <div>Notifications</div>
    </NamedLink>
  ) : null;

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  // Define user type checks with useMemo for optimization
  const isApproved = useMemo(() => isApprovedSeller(currentUser), [currentUser]);
  const userIsSeller = useMemo(() => isSeller(currentUser), [currentUser]);
  const isOwner = useMemo(() => isApprovedOwner(currentUser), [currentUser]);
  const userHasGroups = useMemo(() => hasGroups(currentUser), [currentUser]);
  const isGuest = useMemo(() => isGuestUser(currentUser), [currentUser]);

  const cartItemCount = useSelector(state =>
    (state.cart?.items || []).reduce((sum, i) => sum + i.quantity, 0)
  );

  const cartIcon = <CartIconBadge count={cartItemCount} />;

  const profileLink =
    authenticatedOnClientSide && !isGuest ? (
      <div className={css.profileIconInside}>
        <div className={css.profileImageContainer}>
          <img src={ProfileIcon} />
        </div>
        <div className={css.profileIconLabel}>Profile</div>
      </div>
    ) : null;

  const profileMenu =
    authenticatedOnClientSide && !isGuest ? (
      <Menu>
        <MenuLabel
          className={classNames(css.profileMenuLabel, css.profileIconContainer)}
          isOpenClassName={css.profileMenuIsOpen}
        >
          {/* <Avatar className={css.avatar} user={currentUser} disableProfileLink /> */}
          {profileLink}
        </MenuLabel>
        <MenuContent className={css.profileMenuContent}>
          <MenuItem key="ManageListingsPage">
            {userIsSeller ? (
              <NamedLink
                className={classNames(css.yourListingsLink, currentPageClass('ManageListingsPage'))}
                name="ManageListingsPage"
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id="TopbarDesktop.yourListingsLink" />
              </NamedLink>
            ) : (
              <></>
            )}
          </MenuItem>
          <MenuItem key="ProfileSettingsPage">
            <NamedLink
              className={classNames(
                css.profileSettingsLink,
                currentPageClass('ProfileSettingsPage')
              )}
              name="ProfileSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
            </NamedLink>
          </MenuItem>
          {/* <MenuItem key="BusinessProfileSettingsPage">
          <NamedLink
            className={classNames(css.businessProfileSettingsLink, currentPageClass('BusinessProfileSettingsPage'))}
            name="BusinessProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.businessProfileSettingsLink" />
          </NamedLink>
        </MenuItem> */}
          <MenuItem key="AccountSettingsPage">
            <NamedLink
              className={classNames(css.yourListingsLink, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="InviteUsersPage">
            {isOwner ? (
              <NamedLink
                className={classNames(css.yourListingsLink, currentPageClass('InviteUsersPage'))}
                name="InviteUsersPage"
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id="UserNav.teamInvites" />
              </NamedLink>
            ) : (
              <></>
            )}
          </MenuItem>
          <MenuItem key="GroupsPage">
            {userHasGroups ? (
              <NamedLink
                className={classNames(css.yourListingsLink, currentPageClass('GroupsPage'))}
                name="GroupsPage"
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id="UserNav.yourTeams" />
              </NamedLink>
            ) : (
              <></>
            )}
          </MenuItem>
          <MenuItem key="logout">
            <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.logout" />
            </InlineTextButton>
          </MenuItem>
        </MenuContent>
      </Menu>
    ) : null;

  const providerSignupLink =
    isAuthenticatedOrJustHydrated || currentPage === 'BecomeASellerPage' ? null : (
      <NamedLink name="BecomeASellerPage" className={css.providerSignupLink}>
        <span className={css.signup}>
          <FormattedMessage id="TopbarDesktop.providerSignup" />
        </span>
      </NamedLink>
    );

  const signupLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="SignupPage" className={css.signupLink}>
      <span className={css.signup}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );

  const loginLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="LoginPage" className={css.loginLink}>
      <span className={css.login}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );

  const additionalLinks = !isLanding ? (
    <div className={css.additionalLinkContainer}>
      <div className={classNames(css.additionalLinksNavBar)}>
        <div>
          <Link className={classNames(css.additionalLink)} href="/p/hunting">
            Hunting
          </Link>
          <Link className={classNames(css.additionalLink)} href="/p/fishing">
            Fishing
          </Link>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div>
      <nav className={classes}>
        <div className={css.logoContainer}>
          <LinkedLogo
            className={css.logoLink}
            layout="desktop"
            alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
          />
        </div>
        {mainNav}
        <div className={css.rightSide}>
          {searchIcon}
          {userIsSeller && !isGuest ? (
            <NamedLink
              className={classNames(css.createListingLink, css.customCreateListingLink)}
              name="NewListingPage"
            >
              <span className={css.createListing}>
                <FormattedMessage id="TopbarDesktop.createListing" />
              </span>
            </NamedLink>
          ) : null}
          {/* Hide inbox link for guests */}
          {!isGuest && inboxLink}
          {profileMenu}
          {loginLink}
          {signupLink}
          {providerSignupLink}
          {/* {cartIcon} */}
        </div>
      </nav>
      {/* {additionalLinks} */}
    </div>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  appConfig: null,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  appConfig: object,
};

export default TopbarDesktop;
